import { useState, useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { recordingModeActive } from "@/lib/recordingMode";
import { useStripe, Elements, PaymentElement, ExpressCheckoutElement, useElements } from "@stripe/react-stripe-js";
import { getStripe } from "@/lib/stripeClient";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { useToast } from "@/hooks/use-toast";
import { useCart } from "@/hooks/useCart";
import { apiRequest } from "@/lib/queryClient";
import { Loader2, ShoppingBag, MapPin, Calendar, Truck, Store, Gift, Zap, Minus, Plus, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { useLocation } from "wouter";
import type { CartItemWithDetails, MadeToOrderField } from "@shared/schema";
import { hasListingPhoto } from "@shared/schema";
import { getListingImages } from "@shared/clothing";
import { ListingPhotoPlaceholder } from "@/components/ListingPhotoPlaceholder";
import { MadeToOrderFields, madeToOrderResponsesComplete } from "@/components/MadeToOrderFields";
import { calculateFees, serviceFeeLabel, cartBulkDiscountTotal, makerInviteFeeDiscount, MAKER_INVITE_FEE_REWARD_TYPE, MAKER_INVITE_MIN_SUBTOTAL } from "@shared/fees";
import { formatWeight, unitDisplayLabel } from "@shared/weight";
import { isSnapEligibleItem, SNAP_PUBLICLY_ENABLED } from "@shared/snap";
import { FoodRiskAck, foodRiskLabel } from "@/components/FoodRiskAck";
import { ServiceFeeInfo } from "@/components/ServiceFeeInfo";
import { EbtPartitionPreview } from "@/components/EbtPartitionPreview";
import { trackEvent } from "@/lib/analytics";
import { Capacitor } from "@capacitor/core";
import { Stripe as CapacitorStripe, GooglePayEventsEnum } from "@capacitor-community/stripe";

if (!import.meta.env.VITE_STRIPE_PUBLIC_KEY) {
  throw new Error("Missing required Stripe key: VITE_STRIPE_PUBLIC_KEY");
}

// Retained only for the over-fee-discount comparison (matches the legacy
// PLATFORM_FEE_PERCENT in server/referral-tiers.ts). The actual buyer fee
// charged is the tiered amount from shared/fees.ts.
const PLATFORM_FEE_PERCENT = 12;
const OVER_FEE_DISCOUNT_CART_CAP_USD = 1000;

// Buyer-facing copy for any checkout/payment-blocking failure. The raw
// technical reason is logged server-side and pushed to the admin, so the
// user only ever sees this calm, reassuring message.
const CHECKOUT_ERROR_MESSAGE =
  "Something went wrong on our end, and our team has already been notified. We are on it. If you are stuck, DM us @stoopdrop on Instagram and we will sort it out fast.";


type BuyerFulfillmentOption = {
  id: string;
  fulfillmentMethod: "seller_pickup" | "drop_zone_pickup" | "local_delivery";
  buyerLabel: string;
  name?: string | null;
  description?: string | null;
  dayLabel?: string | null;
  specificDate?: string | null;
  timeSlots?: {
    id: string;
    dayOfWeek: string;
    startTime: string;
    endTime: string;
  }[];
  dropZone?: {
    id: string;
    name: string;
    city: string;
    state: string;
    zipCode: string;
    pickupWindowLabel?: string | null;
  } | null;
};

type SellerFulfillmentGroup = {
  groupId: string;
  sellerId: string;
  sellerName: string;
  fulfillmentProfileId: string;
  items: {
    itemId: string;
    name: string;
    qty: number;
    optionIds: string[];
  }[];
  availableOptions: BuyerFulfillmentOption[];
};

type SellerSelection = {
  fulfillmentProfileId: string;
  scheduledDate: string;
  timeWindowLabel: string;
};

function CheckoutForm({
  onSuccess,
  total,
  disabled,
  orderId,
  clientSecret,
}: {
  onSuccess: () => void;
  total: number;
  disabled: boolean;
  orderId: string | null;
  clientSecret: string;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const { toast } = useToast();
  const { user } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  // Native Google Pay via @capacitor-community/stripe. Only offered when the
  // installed binary actually ships the plugin (older installs fall back to the
  // card form) and the device reports Google Pay availability. The WebView's
  // Payment Element can never render Google Pay, so this native sheet is the
  // only in-app path.
  const [googlePayReady, setGooglePayReady] = useState(false);
  // The official Google Pay button, rendered by Google's PayButton API — the
  // brand guidelines forbid self-made buttons (font, color, radius, padding
  // may not be altered), and the production-access review checks exactly this.
  // pay.js only draws the button; the tap still runs the native plugin flow.
  const gpayButtonRef = useRef<HTMLDivElement | null>(null);
  const gpayMountedRef = useRef(false);
  const handleGooglePayRef = useRef<() => void>(() => {});
  useEffect(() => {
    if (!googlePayReady || gpayMountedRef.current) return;
    let cancelled = false;
    let tries = 0;
    const mount = () => {
      if (cancelled || gpayMountedRef.current) return;
      const api = (window as any).google?.payments?.api;
      const host = gpayButtonRef.current;
      if (!api || !host) {
        if (tries++ < 20) window.setTimeout(mount, 250);
        return;
      }
      try {
        const client = new api.PaymentsClient({ environment: "PRODUCTION" });
        // Guidelines: light button on dark backgrounds, dark on light — a
        // button that blends into the background is an explicit "don't".
        const dark = document.documentElement.classList.contains("dark");
        const button = client.createButton({
          onClick: () => handleGooglePayRef.current(),
          buttonColor: dark ? "white" : "black",
          buttonType: "pay",
          buttonSizeMode: "fill",
        });
        host.innerHTML = "";
        host.appendChild(button);
        gpayMountedRef.current = true;
      } catch { /* no button beats a non-compliant one */ }
    };
    mount();
    return () => {
      cancelled = true;
    };
  }, [googlePayReady]);
  // Every exit below is silent, which made "no Google Pay button" undebuggable
  // on a real device. The trace renders for the admin only (and never while
  // recording mode is on), so the gate can say which step killed it.
  const [gpTrace, setGpTrace] = useState("starting");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!Capacitor.isNativePlatform()) {
          setGpTrace("not native platform");
          return;
        }
        if (!Capacitor.isPluginAvailable("Stripe")) {
          setGpTrace("Stripe plugin NOT in this binary");
          return;
        }
        if (Capacitor.getPlatform() !== "android") {
          setGpTrace("not android");
          return;
        }
        const key = import.meta.env.VITE_STRIPE_PUBLIC_KEY;
        if (!key) {
          setGpTrace("VITE_STRIPE_PUBLIC_KEY missing from bundle");
          return;
        }
        setGpTrace(`initializing (key ${String(key).slice(0, 8)}…)`);
        await CapacitorStripe.initialize({ publishableKey: key });
        setGpTrace("initialized; checking availability");
        await CapacitorStripe.isGooglePayAvailable();
        setGpTrace("available");
        if (!cancelled) setGooglePayReady(true);
      } catch (err) {
        setGpTrace(`failed: ${err instanceof Error ? err.message : String(err)}`);
        /* not available — card form remains */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleGooglePay = async () => {
    if (disabled || isProcessing) return;
    setIsProcessing(true);
    trackEvent("payment_submitted", "/checkout", { orderId, total, wallet: "google_pay" });
    if (orderId) {
      try {
        sessionStorage.setItem("marketlink.lastCheckoutOrderId", orderId);
      } catch { /* private mode */ }
    }
    try {
      await CapacitorStripe.createGooglePay({ paymentIntentClientSecret: clientSecret });
      const { paymentResult } = await CapacitorStripe.presentGooglePay();
      if (paymentResult === GooglePayEventsEnum.Completed) {
        trackEvent("payment_succeeded", "/checkout", { orderId, total, wallet: "google_pay" });
        onSuccess();
        return;
      }
      if (paymentResult === GooglePayEventsEnum.Failed) {
        trackEvent("payment_failed", "/checkout", { orderId, total, wallet: "google_pay" });
        toast({
          title: "Google Pay failed",
          description: "The payment didn't go through. Try a card below.",
          variant: "destructive",
        });
      }
    } catch (err: any) {
      trackEvent("payment_failed", "/checkout", { orderId, total, wallet: "google_pay", message: err?.message });
      toast({
        title: "Google Pay unavailable",
        description: "Use the card form below instead.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };
  handleGooglePayRef.current = handleGooglePay;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements || disabled) return;

    if (!paymentComplete) {
      trackEvent("payment_blocked", "/checkout", { reason: "payment_method_not_confirmed", orderId });
      toast({
        title: "Confirm your payment method",
        description:
          'Tap "Use this card" above to confirm your saved card, or enter your card details, before paying.',
      });
      return;
    }

    setIsProcessing(true);
    await confirmCard("card");
  };

  // Shared by the card form and the wallet buttons so a wallet payment gets the
  // same analytics, the same order stash and the same success path — the latter
  // matters because leaving checkout has to be a full page load (trap #8).
  const confirmCard = async (wallet: "card" | "express") => {
    if (!stripe || !elements) return;
    trackEvent("payment_submitted", "/checkout", { orderId, total, wallet });

    if (orderId) {
      try {
        sessionStorage.setItem("marketlink.lastCheckoutOrderId", orderId);
      } catch { /* private mode */ }
    }

    const returnUrl = orderId
      ? `${window.location.origin}/order-confirmation?orderId=${orderId}`
      : `${window.location.origin}/order-confirmation`;

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: returnUrl,
      },
      redirect: "if_required",
    });

    if (error) {
      // Capture the exact Stripe failure — the toast auto-dismisses, so this
      // is the only durable record of what the buyer actually saw.
      console.error("[checkout] stripe confirmPayment failed", error);
      trackEvent("payment_failed", "/checkout", {
        orderId,
        total,
        message: error.message,
        code: error.code,
        declineCode: (error as any).decline_code,
        type: error.type,
      });
      toast({
        title: "Payment Failed",
        description: error.message,
        variant: "destructive",
      });
      setIsProcessing(false);
    } else {
      trackEvent("payment_succeeded", "/checkout", { orderId, total });
      onSuccess();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {Boolean((user as any)?.isDemoAdmin) && !recordingModeActive() && (
        <p className="text-[11px] text-muted-foreground">GP gate: {gpTrace}</p>
      )}
      {googlePayReady && (
        <div className="space-y-2">
          <div
            ref={gpayButtonRef}
            className={isProcessing || disabled ? "pointer-events-none opacity-50" : ""}
            style={{ minHeight: 48, margin: 8 }}
            data-testid="button-google-pay"
          />
          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground">or pay with card</span>
            <div className="h-px flex-1 bg-border" />
          </div>
        </div>
      )}
      {/* Apple Pay / Google Pay are not rows inside the Payment Element — they
          only surface through the Express Checkout Element. Without this the
          wallets never appeared on the web no matter how Stripe was configured.
          It renders nothing when the device has no wallet, and inside the
          wrapped app it stays empty because a WebView can't offer one — that is
          what the native Google Pay button above is for. */}
      {!googlePayReady && (
        <ExpressCheckoutElement
          options={{ buttonHeight: 48 }}
          onConfirm={async () => {
            setIsProcessing(true);
            await confirmCard("express");
          }}
        />
      )}
      <div className="space-y-4">
        <h3 className="font-semibold">Payment Details</h3>
        <PaymentElement onChange={(event) => setPaymentComplete(event.complete)} />
      </div>

      <Button
        type="submit"
        className="w-full"
        size="lg"
        disabled={!stripe || isProcessing || disabled}
        data-testid="button-complete-payment"
      >
        {isProcessing ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Processing...
          </>
        ) : (
          `Pay $${total.toFixed(2)}`
        )}
      </Button>
    </form>
  );
}

function getWeekdayName(dateString: string) {
  if (!dateString) return "";
  const date = new Date(`${dateString}T12:00:00`);
  return date.toLocaleDateString("en-US", { weekday: "long" });
}

const WEEKDAY_INDEX: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

const WEEKDAY_ALIASES: Record<string, string[]> = {
  sunday: ["sun", "sunday"],
  monday: ["mon", "monday"],
  tuesday: ["tue", "tues", "tuesday"],
  wednesday: ["wed", "wednesday"],
  thursday: ["thu", "thur", "thurs", "thursday"],
  friday: ["fri", "friday"],
  saturday: ["sat", "saturday"],
};

function isDateAllowed(dateString: string, dayLabel?: string | null, specificDate?: string | null) {
  if (!dateString) return false;
  // A one-time option ignores weekday rules — only its own date is bookable.
  if (specificDate) return dateString === specificDate;
  const normalizedLabel = String(dayLabel || "").trim().toLowerCase();

  if (!normalizedLabel || normalizedLabel === "any day") {
    return true;
  }

  const weekday = getWeekdayName(dateString).toLowerCase();
  const aliases = WEEKDAY_ALIASES[weekday] || [weekday];
  return aliases.some((alias) => normalizedLabel.includes(alias));
}

function getAllowedWeekdaySet(option: BuyerFulfillmentOption | undefined): Set<number> | null {
  if (!option) return null;

  const slots = option.timeSlots || [];
  if (slots.length > 0) {
    const indices = new Set<number>();
    for (const slot of slots) {
      const idx = WEEKDAY_INDEX[String(slot.dayOfWeek || "").trim().toLowerCase()];
      if (typeof idx === "number") indices.add(idx);
    }
    if (indices.size > 0) return indices;
  }

  const normalizedLabel = String(option.dayLabel || "").trim().toLowerCase();
  if (!normalizedLabel || normalizedLabel === "any day") return null;

  const indices = new Set<number>();
  for (const [name, aliases] of Object.entries(WEEKDAY_ALIASES)) {
    if (aliases.some((alias) => normalizedLabel.includes(alias))) {
      indices.add(WEEKDAY_INDEX[name]);
    }
  }
  return indices.size > 0 ? indices : null;
}

function formatTimeLabel(value: string) {
  if (!value) return "";
  const [hourRaw, minute] = value.split(":");
  const hour = Number(hourRaw);
  const suffix = hour >= 12 ? "PM" : "AM";
  const normalizedHour = hour % 12 === 0 ? 12 : hour % 12;
  return `${normalizedHour}:${minute} ${suffix}`;
}

function getTimeSlotLabel(startTime: string, endTime: string) {
  return `${formatTimeLabel(startTime)} - ${formatTimeLabel(endTime)}`;
}

function getAvailableTimeWindowOptions(
  selectedOption: BuyerFulfillmentOption | undefined,
  scheduledDate: string
) {
  if (!selectedOption) return [];

  const slots = selectedOption.timeSlots || [];
  if (!scheduledDate || slots.length === 0) return [];

  const weekday = getWeekdayName(scheduledDate).toLowerCase();

  return slots
    .filter((slot) => slot.dayOfWeek.toLowerCase() === weekday)
    .map((slot) => ({
      id: slot.id,
      label: getTimeSlotLabel(slot.startTime, slot.endTime),
    }));
}

function getOptionIcon(method: BuyerFulfillmentOption["fulfillmentMethod"]) {
  if (method === "local_delivery") return <Truck className="mt-0.5 h-4 w-4 text-muted-foreground" />;
  return <MapPin className="mt-0.5 h-4 w-4 text-muted-foreground" />;
}

const CHECKOUT_PREFS_KEY = "stoop:checkoutPrefs";

function readCheckoutPrefs(): {
  deliveryMode?: "per_seller" | "community_delivery";
  pickupKind?: "seller" | "hub" | null;
  deliveryAddress?: { line1: string; line2?: string; city: string; state: string; zip: string; lat: number; lng: number };
} {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.sessionStorage.getItem(CHECKOUT_PREFS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export default function Checkout() {
  const stripePromise = useMemo(() => getStripe(), []);
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { cart, cartSessionId, clearCart, updateQuantity, updateCustomResponses, removeFromCart, hydrated: cartHydrated } = useCart();
  const [clientSecret, setClientSecret] = useState("");
  const [orderId, setOrderId] = useState<string | null>(null);
  const [paymentIntentError, setPaymentIntentError] = useState("");
  const [cartFixNotice, setCartFixNotice] = useState<{ title: string; message: string } | null>(null);
  const [sellerSelections, setSellerSelections] = useState<Record<string, SellerSelection>>({});
  // Restore the buyer's fulfillment choice across checkout re-entries. Without
  // this, a failed payment that drops the buyer back into a fresh checkout
  // silently reverts to per-seller pickup — so they can re-pay and unknowingly
  // switch a community-delivery order to pickup.
  const savedCheckoutPrefs = useMemo(() => readCheckoutPrefs(), []);
  const [deliveryMode, setDeliveryMode] = useState<"per_seller" | "community_delivery">(
    savedCheckoutPrefs.deliveryMode ?? "per_seller",
  );
  // When deliveryMode is per_seller, this narrows which fulfillment
  // method type the buyer wants ("seller" pickup vs "hub" pickup). Used
  // to filter group.availableOptions below so only matching options
  // appear under each seller. null = show all (buyer hasn't picked yet).
  const [pickupKind, setPickupKind] = useState<"seller" | "hub" | null>(
    savedCheckoutPrefs.pickupKind ?? "seller",
  );
  const [deliveryAddress, setDeliveryAddress] = useState({
    line1: "",
    line2: "",
    city: "",
    state: "",
    zip: "",
    lat: 0,
    lng: 0,
    ...(savedCheckoutPrefs.deliveryAddress ?? {}),
  });
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [geocodeAttempted, setGeocodeAttempted] = useState(false);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(
        CHECKOUT_PREFS_KEY,
        JSON.stringify({ deliveryMode, pickupKind, deliveryAddress }),
      );
    } catch {
      /* private mode */
    }
  }, [deliveryMode, pickupKind, deliveryAddress]);

  const { data: items = [], isLoading: itemsLoading, isSuccess: itemsResolved } = useQuery<CartItemWithDetails[]>({
    queryKey: ["/api/cart/details", JSON.stringify(cart)],
    queryFn: async () => {
      if (cart.length === 0) return [];
      const res = await fetch(`/api/cart/details?cart=${encodeURIComponent(JSON.stringify(cart))}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    enabled: cart.length > 0,
  });

  // cart/details drops any unavailable item (unpublished/archived/deleted), so
  // such a line becomes an invisible "ghost" — the buyer can't see it to remove
  // it, yet it stays in the cart and blocks checkout on every attempt. Prune any
  // cart entry the server didn't return the moment details resolve, so a ghost
  // can never wedge the order in the first place.
  useEffect(() => {
    if (!itemsResolved || cart.length === 0) return;
    const present = new Set(items.map((ci) => ci.itemId));
    const unavailableIds = new Set(
      items.filter((ci) => (ci as any).unavailable).map((ci) => ci.itemId),
    );
    const ghosts = cart.filter((c) => !present.has(c.itemId) || unavailableIds.has(c.itemId));
    if (ghosts.length === 0) return;
    ghosts.forEach((g) => removeFromCart(g.itemId, g.weightUnitId));
    toast({
      title: ghosts.length === 1 ? "Removed an unavailable item" : "Removed unavailable items",
      description:
        "An item in your cart is no longer available, so we took it out. The rest of your order is ready to check out.",
    });
  }, [itemsResolved, items, cart, removeFromCart, toast]);

  // Higher-risk food in the cart requires a one-time assumption-of-risk
  // acknowledgment before the payment intent (and order) is created.
  const [foodRiskAck, setFoodRiskAck] = useState(false);
  const riskyLabel = useMemo(() => foodRiskLabel(items.map((i) => i.item.category)), [items]);
  const needsFoodRiskAck = riskyLabel !== null;

  const { data: fulfillmentGroups = [], isLoading: fulfillmentLoading } = useQuery<SellerFulfillmentGroup[]>({
    queryKey: ["/api/cart/fulfillment-options", JSON.stringify(cart)],
    queryFn: async () => {
      if (cart.length === 0) return [];
      const res = await fetch(
        `/api/cart/fulfillment-options?cart=${encodeURIComponent(JSON.stringify(cart))}`,
        {
          credentials: "include",
        }
      );
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    enabled: cart.length > 0,
  });

  type Reward = { id: string; label: string; discountPercent: number; type: string };
  const { data: availableRewards = [] } = useQuery<Reward[]>({
    queryKey: ["/api/my/rewards"],
    queryFn: async () => {
      const res = await fetch("/api/my/rewards", { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    staleTime: 60_000,
  });
  const [appliedRewardId, setAppliedRewardId] = useState<string | null>(null);
  const appliedReward =
    appliedRewardId
      ? availableRewards.find((r) => r.id === appliedRewardId) ?? null
      : null;

  const sellerLocations = useMemo(() => {
    const locs: { lat: number; lng: number }[] = [];
    const seen = new Set<string>();
    for (const group of fulfillmentGroups) {
      if (!seen.has(group.sellerId)) {
        seen.add(group.sellerId);
        const seller = (group as any).sellerLat && (group as any).sellerLng
          ? { lat: Number((group as any).sellerLat), lng: Number((group as any).sellerLng) }
          : null;
        if (seller) locs.push(seller);
      }
    }
    return locs;
  }, [fulfillmentGroups]);

  // Geocode the delivery address when it changes. Address fields gate the
  // pricing query (lat/lng must be non-zero), which in turn gates the Pay
  // button. If Nominatim fails or returns no result the user is stuck
  // unless we surface that — see missingCheckoutDetails below.
  useEffect(() => {
    if (deliveryMode !== "community_delivery") return;
    const { line1, city, state, zip } = deliveryAddress;
    if (!line1.trim() || !city.trim() || !zip.trim()) return;

    setGeocodeAttempted(false);
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setIsGeocoding(true);
      try {
        const query = [line1, city, state, zip, "USA"].filter(Boolean).join(", ");
        const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=jsonv2&limit=1&countrycodes=us`;
        const res = await fetch(url, { signal: controller.signal, headers: { Accept: "application/json" } });
        if (!res.ok) return;
        const results = await res.json() as { lat?: string; lon?: string }[];
        const first = results[0];
        if (first?.lat && first?.lon) {
          setDeliveryAddress((prev) => ({
            ...prev,
            lat: Number(first.lat),
            lng: Number(first.lon),
          }));
        }
      } catch {
        /* swallowed — surfaced via geocodeAttempted + lat/lng=0 below */
      } finally {
        setIsGeocoding(false);
        setGeocodeAttempted(true);
      }
    }, 800);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [deliveryMode, deliveryAddress.line1, deliveryAddress.city, deliveryAddress.state, deliveryAddress.zip]);

  // Single-seller id + subtotal feed the founder free-delivery mirror so the
  // preview delivery fee matches the server charge (price-mismatch guard).
  const distinctSellerIds = Array.from(new Set(fulfillmentGroups.map((g) => g.sellerId)));
  const estimateSellerId = distinctSellerIds.length === 1 ? distinctSellerIds[0] : "";
  const estimateSubtotal = items.reduce((sum, item) => sum + item.lineSubtotal, 0);

  type PricingEstimate = { available: boolean; deliveryFee?: number; driverPay?: number; estimatedMiles?: number; estimatedStops?: number; reason?: string; founderFreeDelivery?: boolean };
  const { data: pricingEstimate } = useQuery<PricingEstimate>({
    queryKey: ["/api/delivery-pricing/estimate", deliveryMode, deliveryAddress.lat, deliveryAddress.lng, JSON.stringify(sellerLocations), estimateSellerId, estimateSubtotal],
    queryFn: async () => {
      const res = await fetch(
        `/api/delivery-pricing/estimate?buyerLat=${deliveryAddress.lat}&buyerLng=${deliveryAddress.lng}&sellerLocations=${encodeURIComponent(JSON.stringify(sellerLocations))}${estimateSellerId ? `&sellerId=${encodeURIComponent(estimateSellerId)}&subtotal=${estimateSubtotal}` : ""}`,
        { credentials: "include" },
      );
      if (!res.ok) return { available: false, reason: "Pricing unavailable" };
      return res.json();
    },
    enabled: deliveryMode === "community_delivery" && sellerLocations.length > 0 && deliveryAddress.lat !== 0 && deliveryAddress.lng !== 0,
    staleTime: 30_000,
  });

  // Log the moment a delivery estimate (fee / free / unavailable) is shown, so
  // in-app delivery friction is as readable as the storefront's.
  useEffect(() => {
    if (deliveryMode === "community_delivery" && pricingEstimate) {
      trackEvent("checkout_delivery_estimate", "/checkout", {
        available: pricingEstimate.available,
        deliveryFee: pricingEstimate.deliveryFee,
        estimatedMiles: pricingEstimate.estimatedMiles,
        founderFreeDelivery: pricingEstimate.founderFreeDelivery,
        reason: pricingEstimate.reason,
      });
    }
  }, [pricingEstimate, deliveryMode]);

  // Log checkout open with cart contents once the cart hydrates.
  useEffect(() => {
    if (cartHydrated && cart.length > 0) {
      trackEvent("checkout_opened", "/checkout", {
        lineCount: cart.length,
        itemCount: cart.reduce((s, c) => s + c.qty, 0),
        items: cart.map((c) => ({ itemId: c.itemId, qty: c.qty })),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartHydrated]);

  const DELIVERY_MINIMUM = 15;
  const subtotal = items.reduce((sum, item) => sum + item.lineSubtotal, 0);
  // Per-seller bulk discount. Uses the same shared helper the server charge
  // uses, so the client total matches (the server rejects a mismatch).
  const bulkDiscount = cartBulkDiscountTotal(
    items.map((i) => ({
      sellerId: (i.item as any).sellerId,
      lineSubtotal: i.lineSubtotal,
      discountThreshold:
        (i.item as any).bulkDiscountThresholdCents != null
          ? (i.item as any).bulkDiscountThresholdCents / 100
          : null,
      discountPercent: (i.item as any).bulkDiscountPercent ?? null,
    })),
  );
  const netSubtotal = Number((subtotal - bulkDiscount).toFixed(2));
  const madeToOrderComplete = useMemo(
    () =>
      items.every((ci) =>
        madeToOrderResponsesComplete(
          (ci.item as any).customFields,
          cart.find((c) => c.itemId === ci.itemId)?.customResponses,
        ),
      ),
    [items, cart],
  );
  const deliveryAllowed = subtotal >= DELIVERY_MINIMUM;
  // Don't show or charge a delivery fee until an address yields a real
  // estimate. Before that, a placeholder fee scares buyers (especially founder
  // buyers who will actually get free delivery) into abandoning checkout.
  const hasDeliveryEstimate = deliveryMode === "community_delivery" && Boolean(pricingEstimate?.available);
  const isFounderFreeDelivery = Boolean(pricingEstimate?.founderFreeDelivery);
  // Made-to-order lead time across the whole cart. Community delivery is a
  // "neighbor delivers within 7 days" model, so the deliver-by clock can't
  // start until the slowest custom item is ready (server mirrors this when it
  // sets deliverByDate). 0 for an all-in-stock cart.
  const maxLeadTimeDays = useMemo(
    () =>
      items.reduce((m, ci) => {
        const it = ci.item as any;
        const lead = Number(it?.isMadeToOrder) === 1 ? Number(it?.leadTimeDays) || 0 : 0;
        return Math.max(m, lead);
      }, 0),
    [items],
  );
  const earliestReadyDate = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + maxLeadTimeDays);
    return d;
  }, [maxLeadTimeDays]);
  const earliestReadyLabel = useMemo(() => format(earliestReadyDate, "MMM d"), [earliestReadyDate]);
  // Community delivery sends one driver to collect from every seller, so the
  // delivery date has to be a weekday each seller can hand off on. Derive each
  // seller's collectable days from their pickup options (a driver collecting is
  // a pickup; a seller's own local_delivery schedule doesn't apply), then
  // intersect across sellers. No shared day, or an "any day" seller, => no
  // weekday restriction so checkout can't dead-end.
  const communityAllowedWeekdays = useMemo<Set<number> | null>(() => {
    let intersection: Set<number> | null = null;
    for (const group of fulfillmentGroups) {
      let sellerDays: Set<number> | null = null;
      let unrestricted = false;
      for (const option of group.availableOptions) {
        if (option.fulfillmentMethod === "local_delivery") continue;
        const days = getAllowedWeekdaySet(option);
        if (days === null) { unrestricted = true; break; }
        if (!sellerDays) sellerDays = new Set(days);
        else days.forEach((d) => sellerDays!.add(d));
      }
      if (unrestricted || !sellerDays) continue;
      if (intersection === null) {
        intersection = new Set(sellerDays);
      } else {
        const next = new Set<number>();
        intersection.forEach((d) => { if (sellerDays!.has(d)) next.add(d); });
        intersection = next;
      }
    }
    return intersection && intersection.size > 0 ? intersection : null;
  }, [fulfillmentGroups]);
  // First selectable delivery date: the ready date, advanced to the next day
  // every seller can hand off on.
  const earliestDeliveryDate = useMemo(() => {
    const d = new Date(earliestReadyDate);
    if (communityAllowedWeekdays) {
      for (let i = 0; i < 14 && !communityAllowedWeekdays.has(d.getDay()); i++) {
        d.setDate(d.getDate() + 1);
      }
    }
    return d;
  }, [earliestReadyDate, communityAllowedWeekdays]);
  const communityDaysLabel = useMemo(() => {
    if (!communityAllowedWeekdays) return null;
    const names = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const ids: number[] = [];
    communityAllowedWeekdays.forEach((i) => ids.push(i));
    return ids.sort((a, b) => a - b).map((i) => names[i]).join(", ");
  }, [communityAllowedWeekdays]);
  // Buyer-chosen earliest delivery date. Defaults to (and can't go before) the
  // first collectable date; a driver delivers within ~6 days after.
  const [preferredDeliveryDate, setPreferredDeliveryDate] = useState<Date | undefined>(undefined);
  useEffect(() => {
    setPreferredDeliveryDate((cur) =>
      cur && cur >= earliestDeliveryDate &&
      (!communityAllowedWeekdays || communityAllowedWeekdays.has(cur.getDay()))
        ? cur
        : earliestDeliveryDate,
    );
  }, [earliestDeliveryDate, communityAllowedWeekdays]);
  const communityDeliveryFee = hasDeliveryEstimate ? (pricingEstimate?.deliveryFee ?? 9) : 0;
  const tierFees = calculateFees({ subtotal: netSubtotal });
  const grossPlatformFee = tierFees.buyerFee;
  const isMakerInviteReward = appliedReward?.type === MAKER_INVITE_FEE_REWARD_TYPE;
  const isOverFeeReward =
    appliedReward && !isMakerInviteReward ? appliedReward.discountPercent > PLATFORM_FEE_PERCENT : false;
  const eligibleSubtotal = isOverFeeReward ? Math.min(netSubtotal, OVER_FEE_DISCOUNT_CART_CAP_USD) : netSubtotal;
  // Maker-invite rewards discount the fee (floored at break-even), not a
  // percent of subtotal — mirror the server so the total matches exactly.
  const referralDiscount = !appliedReward
    ? 0
    : isMakerInviteReward
    ? makerInviteFeeDiscount(netSubtotal, appliedReward.discountPercent)
    : eligibleSubtotal * (appliedReward.discountPercent / 100);
  const platformFee = grossPlatformFee;
  const total = netSubtotal + grossPlatformFee - referralDiscount + communityDeliveryFee;
  // Sum of line items that are both a SNAP-eligible category and sold by a
  // SNAP-approved seller. Zero (and the block below hidden) unless a seller
  // has been flagged approved — see users.snapApproved.
  const ebtEligibleSubtotal = items.reduce((sum, ci) => {
    // Behind the same master switch as every other EBT surface, so a seller row
    // flagged approved can't surface split-tender before FNS/USDA authorization.
    if (!SNAP_PUBLICLY_ENABLED) return sum;
    const seller = (ci.item as any).seller;
    const sellerApproved = Number(seller?.snapApproved ?? 0) === 1;
    if (sellerApproved && isSnapEligibleItem(ci.item.category, ci.item.subtype)) {
      return sum + ci.lineSubtotal;
    }
    return sum;
  }, 0);

  useEffect(() => {
    setSellerSelections((current) => {
      const next = { ...current };
      let changed = false;

      for (const group of fulfillmentGroups) {
        const existing = next[group.groupId];
        const firstOption = group.availableOptions[0];

        if (!existing && firstOption) {
          next[group.groupId] = {
            fulfillmentProfileId: firstOption.id,
            scheduledDate: "",
            timeWindowLabel: (firstOption.timeSlots?.length ?? 0) === 0 ? "Any time" : "",
          };
          changed = true;
          continue;
        }

        if (existing) {
          const selectedOption = group.availableOptions.find(
            (option) => option.id === existing.fulfillmentProfileId
          );
          const hasNoSlots = selectedOption && (selectedOption.timeSlots?.length ?? 0) === 0;
          if (hasNoSlots && existing.timeWindowLabel !== "Any time") {
            next[group.groupId] = { ...existing, timeWindowLabel: "Any time" };
            changed = true;
          }
        }
      }

      return changed ? next : current;
    });
  }, [fulfillmentGroups, sellerSelections]);

  const selectionState = useMemo(() => {
    const states = fulfillmentGroups.map((group) => {
      const selection = sellerSelections[group.groupId];
      const selectedOption = group.availableOptions.find(
        (option) => option.id === selection?.fulfillmentProfileId
      );

      const hasRequiredFields = Boolean(
        selection?.fulfillmentProfileId && selection?.scheduledDate && selection?.timeWindowLabel
      );

      const dateAllowed = Boolean(
        selection?.scheduledDate &&
          selectedOption &&
          isDateAllowed(selection.scheduledDate, selectedOption.dayLabel, selectedOption.specificDate)
      );

      return {
        sellerId: group.sellerId,
        groupId: group.groupId,
        selectedOption,
        hasRequiredFields,
        dateAllowed,
        complete: Boolean(selectedOption && hasRequiredFields && dateAllowed),
      };
    });

    const perSellerComplete =
      fulfillmentGroups.length > 0 &&
      states.length === fulfillmentGroups.length &&
      states.every((entry) => entry.complete);

    const communityDeliveryComplete =
      deliveryMode === "community_delivery" &&
      deliveryAddress.line1.trim().length > 0 &&
      deliveryAddress.city.trim().length > 0 &&
      deliveryAddress.state.trim().length > 0 &&
      deliveryAddress.zip.trim().length > 0 &&
      deliveryAddress.lat !== 0 &&
      deliveryAddress.lng !== 0 &&
      Boolean(pricingEstimate?.available);

    return {
      states,
      allComplete: deliveryMode === "community_delivery" ? communityDeliveryComplete : perSellerComplete,
    };
  }, [fulfillmentGroups, sellerSelections, deliveryMode, deliveryAddress, pricingEstimate?.available]);

  const missingCheckoutDetails = useMemo<string[]>(() => {
    if (selectionState.allComplete) return [];

    if (deliveryMode === "community_delivery") {
      if (sellerLocations.length === 0) {
        return [
          "None of the sellers in your cart have an address on file for community delivery yet. Pick a pickup option above instead.",
        ];
      }
      const missingFields: string[] = [];
      if (!deliveryAddress.line1.trim()) missingFields.push("street address");
      if (!deliveryAddress.city.trim()) missingFields.push("city");
      if (!deliveryAddress.state.trim()) missingFields.push("state");
      if (!deliveryAddress.zip.trim()) missingFields.push("ZIP code");
      if (missingFields.length > 0) {
        return [`Enter your ${missingFields.join(", ")}.`];
      }
      if (isGeocoding || !geocodeAttempted) {
        return ["Looking up your delivery address…"];
      }
      if (deliveryAddress.lat === 0 || deliveryAddress.lng === 0) {
        return ["We couldn't find that address. Double-check the spelling or try a more specific street."];
      }
      if (!pricingEstimate) {
        return ["Loading delivery pricing…"];
      }
      if (!pricingEstimate.available) {
        return [pricingEstimate.reason || "Community delivery isn't available to this address yet. Pick a pickup option above instead."];
      }
      return [];
    }

    const items: string[] = [];
    for (const state of selectionState.states) {
      if (state.complete) continue;
      const group = fulfillmentGroups.find((g) => g.groupId === state.groupId);
      const sellerName = group?.sellerName || "this seller";
      if (!state.selectedOption) {
        items.push(`Choose a pickup option for ${sellerName}.`);
        continue;
      }
      const selection = sellerSelections[state.groupId];
      if (!selection?.scheduledDate) {
        items.push(`Pick a date for ${sellerName}.`);
      } else if (!state.dateAllowed && state.selectedOption.dayLabel) {
        items.push(`Pick a date in ${state.selectedOption.dayLabel} for ${sellerName}.`);
      } else if (!selection?.timeWindowLabel) {
        items.push(`Pick a time window for ${sellerName}.`);
      }
    }
    return items;
  }, [
    selectionState,
    deliveryMode,
    deliveryAddress,
    isGeocoding,
    geocodeAttempted,
    pricingEstimate,
    fulfillmentGroups,
    sellerSelections,
    sellerLocations,
  ]);

  const fulfillmentSelectionsPayload = useMemo(
    () =>
      fulfillmentGroups
        .map((group) => {
          const selection = sellerSelections[group.groupId];
          if (
            !selection?.fulfillmentProfileId ||
            !selection?.scheduledDate ||
            !selection?.timeWindowLabel
          ) {
            return null;
          }

          return {
            groupId: group.groupId,
            sellerId: group.sellerId,
            fulfillmentProfileId: selection.fulfillmentProfileId,
            scheduledDate: selection.scheduledDate,
            timeWindowLabel: selection.timeWindowLabel,
          };
        })
        .filter(Boolean),
    [fulfillmentGroups, sellerSelections]
  );

  useEffect(() => {
    let cancelled = false;

    async function createPaymentIntent() {
      if (items.length === 0 || clientSecret || !selectionState.allComplete || !madeToOrderComplete) return;
      if (needsFoodRiskAck && !foodRiskAck) return;

      try {
        setPaymentIntentError("");

        const res = await apiRequest("POST", "/api/create-payment-intent", {
          amount: total,
          items: cart,
          cartSessionId,
          fulfillmentSelections: deliveryMode === "per_seller" ? fulfillmentSelectionsPayload : [],
          deliveryMethod: deliveryMode === "community_delivery" ? "community_delivery" : undefined,
          deliveryAddress: deliveryMode === "community_delivery" ? {
            addressLine1: deliveryAddress.line1,
            addressLine2: deliveryAddress.line2.trim() || undefined,
            city: deliveryAddress.city,
            state: deliveryAddress.state,
            zipCode: deliveryAddress.zip,
            buyerLat: deliveryAddress.lat,
            buyerLng: deliveryAddress.lng,
          } : undefined,
          deliveryFee: deliveryMode === "community_delivery" ? communityDeliveryFee : undefined,
          appliedRewardId,
          preferredDeliveryDate:
            deliveryMode === "community_delivery" && preferredDeliveryDate
              ? format(preferredDeliveryDate, "yyyy-MM-dd")
              : undefined,
          foodRiskAck: needsFoodRiskAck ? true : undefined,
        });

        const data = await res.json();

        if (!cancelled) {
          setClientSecret(data.clientSecret);
          setOrderId(data.orderId);
          trackEvent("checkout_intent_created", "/checkout", {
            orderId: data.orderId,
            cartSessionId,
            deliveryMode,
            total,
            itemCount: cart.reduce((s, c) => s + c.qty, 0),
            buyerFee: tierFees.buyerFee,
            deliveryFee: communityDeliveryFee,
          });
        }
      } catch (error: any) {
        if (!cancelled) {
          console.error("[checkout] create-payment-intent failed", error);
          const body = error?.body as { itemId?: string; qtyAvailable?: number } | undefined;
          trackEvent("checkout_intent_failed", "/checkout", {
            deliveryMode,
            total,
            message: error?.message,
            status: error?.status,
          });
          if (body?.itemId) {
            // One specific item is blocking checkout (sold out, unpublished, or
            // over-ordered). Fix the cart automatically and make the buyer
            // acknowledge exactly what changed — a toast flashes past too fast,
            // and re-running the intent below will now succeed on the good items.
            const blockedName =
              items.find((ci) => ci.item?.id === body.itemId)?.item?.name || "An item in your cart";
            if (typeof body.qtyAvailable === "number") {
              updateQuantity(body.itemId, body.qtyAvailable);
              setCartFixNotice({
                title: "Quantity adjusted",
                message: `Only ${body.qtyAvailable} of “${blockedName}” ${
                  body.qtyAvailable === 1 ? "is" : "are"
                } available right now, so we lowered the amount in your cart. Tap OK and your order is ready to check out.`,
              });
            } else {
              removeFromCart(body.itemId);
              setCartFixNotice({
                title: "Item no longer available",
                message: `“${blockedName}” is no longer available, so we removed it from your cart. Tap OK and the rest of your order is ready to check out.`,
              });
            }
            return;
          }
          setPaymentIntentError(CHECKOUT_ERROR_MESSAGE);
          toast({
            title: "Checkout hiccup",
            description: CHECKOUT_ERROR_MESSAGE,
            variant: "destructive",
          });
        }
      }
    }

    createPaymentIntent();
    return () => {
      cancelled = true;
    };
  }, [
    items,
    cart,
    total,
    clientSecret,
    toast,
    selectionState.allComplete,
    fulfillmentSelectionsPayload,
    needsFoodRiskAck,
    foodRiskAck,
  ]);

  // Changing the applied reward changes the total, so the existing payment
  // intent (which Stripe locked to the previous amount) becomes stale.
  // Clear it to force a fresh /api/create-payment-intent with the new
  // appliedRewardId.
  useEffect(() => {
    setClientSecret("");
    setOrderId(null);
  }, [appliedRewardId, preferredDeliveryDate]);

  useEffect(() => {
    function emit() {
      if (selectionState.allComplete) {
        window.dispatchEvent(new CustomEvent("mission:checkout-fulfillment"));
      }
    }
    emit();
    function onRecheck(ev: Event) {
      const detail = (ev as CustomEvent).detail as { event?: string } | undefined;
      if (!detail || detail.event === "mission:checkout-fulfillment") emit();
    }
    window.addEventListener("marketlink:mission-recheck", onRecheck);
    return () => window.removeEventListener("marketlink:mission-recheck", onRecheck);
  }, [selectionState.allComplete]);

  useEffect(() => {
    if (!cartHydrated) return;
    if (!itemsLoading && items.length === 0) {
      toast({
        title: "Cart is empty",
        description: "Add items to your cart before checking out",
        variant: "destructive",
      });
      setLocation("/");
    }
  }, [cartHydrated, items, itemsLoading, setLocation, toast]);

  const handlePaymentSuccess = async () => {
    try {
      if (orderId) {
        await apiRequest("POST", `/api/orders/${orderId}/mark-paid`);
      }
    } catch (error: any) {
      console.error("[checkout] mark-paid failed after payment success", error);
      trackEvent("checkout_mark_paid_failed", "/checkout", { orderId, message: error?.message });
      toast({
        title: "Order update warning",
        description: error?.message || "Payment succeeded, but order status could not be updated.",
        variant: "destructive",
      });
    }

    clearCart();
    try {
      window.sessionStorage.removeItem(CHECKOUT_PREFS_KEY);
    } catch {
      /* private mode */
    }
    toast({
      title: "Payment Successful",
      description: "Thank you for your order!",
    });
    window.location.replace(`/order-confirmation?orderId=${orderId}`);
  };

  if (itemsLoading || fulfillmentLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (paymentIntentError) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="container mx-auto flex flex-1 items-center justify-center px-4 py-8">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle>Checkout unavailable</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">{paymentIntentError}</p>
              <Button onClick={() => setLocation("/")}>Return to marketplace</Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background overflow-x-hidden">
      <AlertDialog open={cartFixNotice !== null}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{cartFixNotice?.title}</AlertDialogTitle>
            <AlertDialogDescription>{cartFixNotice?.message}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setCartFixNotice(null)} data-testid="button-cart-fix-ok">
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Header />

      <main className="flex-1 px-4 py-8 overflow-x-hidden">
        <div className="mx-auto max-w-6xl w-full min-w-0">
          <h1 className="mb-8 text-3xl font-semibold" data-testid="text-checkout-title">
            Checkout
          </h1>

          <div className="grid gap-8 lg:grid-cols-3 min-w-0">
            <div className="lg:order-1 lg:col-span-1 min-w-0">
              <Card className="lg:sticky lg:top-24">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ShoppingBag className="h-5 w-5" />
                    Order Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {items.some((ci) => (ci.item as any).isMadeToOrder === 1) && (
                    <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
                      <div className="text-sm font-semibold text-primary">
                        Made to order — customized item
                      </div>
                      <p className="mt-1 text-xs leading-5 text-muted-foreground">
                        One or more items below are made just for you, not sold from stock.
                        Add or review your customization for each, and the maker confirms the
                        details with you in your order chat. It's ready after the lead time, so
                        pick a pickup date on or after the earliest date shown below.
                      </p>
                    </div>
                  )}
                  <div className="space-y-3">
                    {items.map((cartItem) => {
                      const price = parseFloat(cartItem.item.price);
                      const unitWeight = (cartItem as any).unitWeightCentiLb as number | null | undefined;
                      const unitLabel = (cartItem as any).unitLabel as string | null | undefined;
                      const isWeightUnit = unitWeight != null;
                      const maxQty = Number(cartItem.item.qtyAvailable ?? 0) || cartItem.qty;
                      // Editing the cart on /checkout invalidates the payment
                      // intent (totals change). Clear clientSecret on qty/remove
                      // so a fresh intent is created with the new total.
                      const editCart = (next: number) => {
                        if (next <= 0) {
                          removeFromCart(cartItem.itemId);
                        } else {
                          updateQuantity(cartItem.itemId, Math.min(next, maxQty));
                        }
                        setClientSecret("");
                      };
                      const lineCustomFields =
                        ((cartItem.item as any).customFields as MadeToOrderField[] | null) ?? [];
                      const lineResponses =
                        cart.find((c) => c.itemId === cartItem.itemId)?.customResponses ?? [];
                      return (
                        <div key={cartItem.itemId} className="flex gap-3">
                          {hasListingPhoto(cartItem.item) ? (
                            <img
                              src={getListingImages(cartItem.item)[0]}
                              alt={cartItem.item.name}
                              className="h-16 w-16 rounded-md object-cover"
                            />
                          ) : (
                            <div className="h-16 w-16 shrink-0 overflow-hidden rounded-md">
                              <ListingPhotoPlaceholder category={cartItem.item.category} size="xs" />
                            </div>
                          )}
                          <div className="min-w-0 flex-1 space-y-1">
                            <p className="truncate text-sm font-medium">{cartItem.item.name}</p>
                            {Number((cartItem.item as any).seller?.snapApproved ?? 0) === 1 &&
                              isSnapEligibleItem(cartItem.item.category, cartItem.item.subtype) && (
                                <span className="inline-block rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400">
                                  EBT eligible
                                </span>
                              )}
                            {isWeightUnit ? (
                              <p className="text-xs text-muted-foreground">
                                {unitDisplayLabel({ label: unitLabel, weightCentiLb: unitWeight })} · ${cartItem.lineSubtotal.toFixed(2)}
                              </p>
                            ) : (
                              <p className="text-xs text-muted-foreground">
                                ${price.toFixed(2)} each
                              </p>
                            )}
                            {isWeightUnit ? (
                              <button
                                type="button"
                                onClick={() => editCart(0)}
                                className="inline-flex h-7 items-center gap-1 rounded-md px-2 text-xs text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                                aria-label="Remove from cart"
                              >
                                <Trash2 className="h-3 w-3" /> Remove
                              </button>
                            ) : (
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => editCart(cartItem.qty - 1)}
                                  className="flex h-7 w-7 items-center justify-center rounded-md border hover:bg-accent"
                                  aria-label="Decrease quantity"
                                >
                                  <Minus className="h-3 w-3" />
                                </button>
                                <span className="min-w-[1.5rem] text-center text-sm font-medium">
                                  {cartItem.qty}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => editCart(cartItem.qty + 1)}
                                  disabled={cartItem.qty >= maxQty}
                                  className="flex h-7 w-7 items-center justify-center rounded-md border hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
                                  aria-label="Increase quantity"
                                >
                                  <Plus className="h-3 w-3" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => editCart(0)}
                                  className="ml-2 flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                                  aria-label="Remove from cart"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </div>
                            )}
                            {lineCustomFields.length > 0 && (
                              <div className="mt-2 space-y-1.5">
                                <div className="text-[10px] font-semibold uppercase tracking-wide text-primary">
                                  Made to order · your customization
                                </div>
                                <MadeToOrderFields
                                  fields={lineCustomFields}
                                  responses={lineResponses}
                                  onChange={(next) =>
                                    updateCustomResponses(cartItem.itemId, next)
                                  }
                                  onCommit={() => setClientSecret("")}
                                />
                              </div>
                            )}
                          </div>
                          <div className="text-sm font-medium">
                            ${cartItem.lineSubtotal.toFixed(2)}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <EbtPartitionPreview eligibleSubtotal={ebtEligibleSubtotal} total={total} />

                  <Separator />

                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span data-testid="text-checkout-subtotal">${subtotal.toFixed(2)}</span>
                    </div>
                    {bulkDiscount > 0 && (
                      <div className="flex justify-between text-sm text-primary">
                        <span>Bulk discount</span>
                        <span data-testid="text-checkout-bulk-discount">-${bulkDiscount.toFixed(2)}</span>
                      </div>
                    )}
                    {appliedReward && referralDiscount > 0 && (
                      <div className="flex justify-between text-sm text-primary">
                        <span className="flex items-center gap-1">
                          <Gift className="h-3.5 w-3.5" />
                          {isMakerInviteReward
                            ? `Service fee reward (${appliedReward.discountPercent}% off fee)`
                            : `Reward applied (${appliedReward.discountPercent}%)`}
                        </span>
                        <span>-${referralDiscount.toFixed(2)}</span>
                      </div>
                    )}
                    {isMakerInviteReward && referralDiscount === 0 && (
                      <div className="text-xs text-muted-foreground">
                        Add ${(MAKER_INVITE_MIN_SUBTOTAL - netSubtotal).toFixed(2)} more to use your
                        service-fee reward (orders ${MAKER_INVITE_MIN_SUBTOTAL}+).
                      </div>
                    )}
                    {hasDeliveryEstimate && (
                      <>
                        <div className="flex justify-between text-sm">
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <Truck className="h-3.5 w-3.5" />
                            Community delivery
                          </span>
                          <span>{isFounderFreeDelivery || communityDeliveryFee === 0 ? "Free" : `$${communityDeliveryFee.toFixed(2)}`}</span>
                        </div>
                        {!isFounderFreeDelivery && communityDeliveryFee > 0 && (
                          <p className="-mt-1 text-[11px] text-muted-foreground">
                            Pays your neighbor driver in full — no tip needed.
                          </p>
                        )}
                      </>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="flex items-center gap-1.5 text-muted-foreground">
                        {serviceFeeLabel(tierFees)}
                        <ServiceFeeInfo subtotal={subtotal} showUpsell />
                      </span>
                      <span data-testid="text-checkout-fee">${platformFee.toFixed(2)}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between text-lg font-semibold">
                      <span>Total</span>
                      <span data-testid="text-checkout-total">${total.toFixed(2)}</span>
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-4 text-sm">
                    <p className="font-medium">
                      {deliveryMode === "community_delivery" ? "Delivery details" : "Pickup or delivery details"}
                    </p>

                    {deliveryMode === "community_delivery" ? (
                      <div className="rounded-lg border p-3 space-y-2">
                        <div className="flex items-center gap-2">
                          <Truck className="h-4 w-4 text-primary" />
                          <p className="font-medium">Community delivery</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <MapPin className="mt-0.5 h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="font-medium">Delivering to</p>
                            <p className="text-muted-foreground">
                              {deliveryAddress.line1}{deliveryAddress.line2.trim() ? ` ${deliveryAddress.line2.trim()}` : ""}{deliveryAddress.city ? `, ${deliveryAddress.city}` : ""}{deliveryAddress.state ? ` ${deliveryAddress.state}` : ""} {deliveryAddress.zip}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <Calendar className="mt-0.5 h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="font-medium">Delivery window</p>
                            <p className="text-muted-foreground">
                              {preferredDeliveryDate
                                ? `From ${format(preferredDeliveryDate, "EEE, MMM d")}, within ~6 days`
                                : "Within ~6 days of your selected date"}
                            </p>
                          </div>
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                          {maxLeadTimeDays > 0
                            ? `Made to order, ready around ${earliestReadyLabel}. A community driver picks up from ${fulfillmentGroups.length} seller${fulfillmentGroups.length === 1 ? "" : "s"} and delivers within ~6 days of your selected date.`
                            : `A community driver picks up from ${fulfillmentGroups.length} seller${fulfillmentGroups.length === 1 ? "" : "s"} and delivers within ~6 days of your selected date.`}
                        </p>
                      </div>
                    ) : (
                      fulfillmentGroups.map((group) => {
                        const selection = sellerSelections[group.groupId];
                        const selectedOption = group.availableOptions.find(
                          (option) => option.id === selection?.fulfillmentProfileId
                        );

                        return (
                          <div key={group.groupId} className="rounded-lg border p-3 space-y-2">
                            <div className="flex items-center gap-2">
                              <Store className="h-4 w-4 text-muted-foreground" />
                              <p className="font-medium">{group.sellerName}</p>
                            </div>

                            {selectedOption ? (
                              <>
                                <div className="flex items-start gap-2">
                                  {getOptionIcon(selectedOption.fulfillmentMethod)}
                                  <div>
                                    <p className="font-medium">{selectedOption.buyerLabel}</p>
                                    {selectedOption.dropZone && (
                                      <p className="text-muted-foreground">
                                        {selectedOption.dropZone.name} — {selectedOption.dropZone.city},{" "}
                                        {selectedOption.dropZone.state}
                                      </p>
                                    )}
                                  </div>
                                </div>

                                <div className="flex items-start gap-2">
                                  <Calendar className="mt-0.5 h-4 w-4 text-muted-foreground" />
                                  <div>
                                    <p className="font-medium">{selectedOption.fulfillmentMethod === "local_delivery" ? "Delivery date" : "Pickup date"}</p>
                                    <p className="text-muted-foreground">
                                      {selection?.scheduledDate || "Not selected"}
                                    </p>
                                  </div>
                                </div>

                                <div className="flex items-start gap-2">
                                  <Calendar className="mt-0.5 h-4 w-4 text-muted-foreground" />
                                  <div>
                                    <p className="font-medium">Time window</p>
                                    <p className="text-muted-foreground">
                                      {selection?.timeWindowLabel || "Not selected"}
                                    </p>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <p className="text-muted-foreground">No fulfillment method selected yet.</p>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="lg:order-2 lg:col-span-2 space-y-6 min-w-0">
              <Card>
                <CardHeader>
                  <CardTitle>Pickup or delivery</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    {([
                      {
                        key: "pickup_seller",
                        icon: <Store className="h-5 w-5 text-emerald-600" />,
                        title: "Pickup from seller",
                        subtitle: "Pick up directly from each seller — choose timing per seller below",
                        priceLabel: "Free",
                        priceClass: "text-emerald-600",
                        disabled: false,
                        onSelect: () => {
                          setDeliveryMode("per_seller");
                          setPickupKind("seller");
                          trackEvent("checkout_method_selected", "/checkout", { method: "pickup_seller", subtotal });
                        },
                        selected: deliveryMode === "per_seller" && pickupKind === "seller",
                      },
                      {
                        key: "pickup_hub",
                        icon: <MapPin className="h-5 w-5 text-emerald-600" />,
                        title: "Pickup at community hub",
                        subtitle: "Meet the seller at a shared neighborhood spot to pick up",
                        priceLabel: "Free",
                        priceClass: "text-emerald-600",
                        disabled: false,
                        onSelect: () => {
                          setDeliveryMode("per_seller");
                          setPickupKind("hub");
                          trackEvent("checkout_method_selected", "/checkout", { method: "pickup_hub", subtotal });
                        },
                        selected: deliveryMode === "per_seller" && pickupKind === "hub",
                      },
                      {
                        key: "community_delivery",
                        icon: <Truck className="h-5 w-5 text-primary" />,
                        title: "Community delivery",
                        subtitle: !deliveryAllowed
                          ? `$${DELIVERY_MINIMUM} minimum order ($${subtotal.toFixed(2)} in cart)`
                          : fulfillmentGroups.length > 0 && sellerLocations.length === 0
                          ? "Sellers in this cart haven't enabled delivery yet"
                          : "A neighbor delivers to you within 7 days",
                        priceLabel: !deliveryAllowed
                          ? "Locked"
                          : fulfillmentGroups.length > 0 && sellerLocations.length === 0
                          ? "Unavailable"
                          : pricingEstimate?.available
                          ? (pricingEstimate.founderFreeDelivery ? "Free" : `$${pricingEstimate.deliveryFee?.toFixed(2)}`)
                          : isGeocoding
                          ? "Calculating…"
                          : "Enter address",
                        priceClass: !deliveryAllowed || (fulfillmentGroups.length > 0 && sellerLocations.length === 0)
                          ? "text-red-500"
                          : "text-foreground",
                        disabled: !deliveryAllowed || (fulfillmentGroups.length > 0 && sellerLocations.length === 0),
                        onSelect: () => {
                          if (!deliveryAllowed) {
                            trackEvent("checkout_method_blocked", "/checkout", { method: "community_delivery", reason: "under_minimum", subtotal, min: DELIVERY_MINIMUM });
                            return;
                          }
                          if (fulfillmentGroups.length > 0 && sellerLocations.length === 0) {
                            trackEvent("checkout_method_blocked", "/checkout", { method: "community_delivery", reason: "no_delivery_sellers" });
                            return;
                          }
                          setDeliveryMode("community_delivery");
                          trackEvent("checkout_method_selected", "/checkout", { method: "community_delivery", subtotal });
                        },
                        selected: deliveryMode === "community_delivery",
                      },
                      {
                        key: "same_day",
                        icon: <Zap className="h-5 w-5 text-muted-foreground" />,
                        title: "Same-day delivery",
                        subtitle: "Get it the same day a driver picks up — premium tier in development",
                        priceLabel: "Coming soon",
                        priceClass: "text-muted-foreground",
                        disabled: true,
                        onSelect: () => {},
                        selected: false,
                      },
                    ] as const).map((opt) => (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={opt.onSelect}
                        disabled={opt.disabled}
                        aria-pressed={opt.selected}
                        className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition ${
                          opt.disabled
                            ? "cursor-not-allowed opacity-60"
                            : opt.selected
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-primary/50"
                        }`}
                      >
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted">
                          {opt.icon}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 text-sm font-medium">
                            <span className="truncate">{opt.title}</span>
                            {opt.key === "same_day" && (
                              <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                                Soon
                              </span>
                            )}
                          </div>
                          <p className="mt-0.5 truncate text-[11px] text-muted-foreground">
                            {opt.subtitle}
                          </p>
                        </div>
                        <div className={`text-sm font-semibold ${opt.priceClass}`}>
                          {opt.priceLabel}
                        </div>
                      </button>
                    ))}

                    {deliveryMode === "community_delivery" && (
                      <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-3">
                        <p className="text-sm font-medium">Delivery address</p>
                        <Input
                          placeholder="Street address"
                          value={deliveryAddress.line1}
                          onChange={(e) => setDeliveryAddress((d) => ({ ...d, line1: e.target.value }))}
                        />
                        <Input
                          placeholder="Apt, suite, unit (optional)"
                          value={deliveryAddress.line2}
                          onChange={(e) => setDeliveryAddress((d) => ({ ...d, line2: e.target.value }))}
                        />
                        <div className="grid grid-cols-5 gap-2">
                          <Input
                            className="col-span-2"
                            placeholder="City"
                            value={deliveryAddress.city}
                            onChange={(e) => setDeliveryAddress((d) => ({ ...d, city: e.target.value }))}
                          />
                          <Input
                            className="col-span-1"
                            placeholder="State"
                            value={deliveryAddress.state}
                            onChange={(e) => setDeliveryAddress((d) => ({ ...d, state: e.target.value }))}
                            maxLength={2}
                          />
                          <Input
                            className="col-span-2"
                            placeholder="ZIP"
                            value={deliveryAddress.zip}
                            onChange={(e) => setDeliveryAddress((d) => ({ ...d, zip: e.target.value }))}
                            maxLength={10}
                          />
                        </div>
                        <div>
                          <p className="text-sm font-medium mb-1">Earliest delivery date</p>
                          {maxLeadTimeDays > 0 && (
                            <p className="text-[11px] text-muted-foreground mb-2">
                              Made to order — ready around {earliestReadyLabel}. You can't pick a date before then.
                            </p>
                          )}
                          {communityDaysLabel && (
                            <p className="text-[11px] text-muted-foreground mb-2">
                              {fulfillmentGroups.length > 1 ? "Sellers hand off" : "Seller hands off"} on {communityDaysLabel} — other days are blocked.
                            </p>
                          )}
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-left text-sm"
                              >
                                <span className={preferredDeliveryDate ? "" : "text-muted-foreground"}>
                                  {preferredDeliveryDate ? format(preferredDeliveryDate, "EEE, MMM d") : "Pick a date"}
                                </span>
                                <Calendar className="h-4 w-4 text-muted-foreground" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <CalendarPicker
                                mode="single"
                                selected={preferredDeliveryDate}
                                defaultMonth={preferredDeliveryDate || earliestDeliveryDate}
                                onSelect={(date) => { if (date) setPreferredDeliveryDate(date); }}
                                disabled={(date) =>
                                  date < earliestReadyDate ||
                                  (communityAllowedWeekdays !== null &&
                                    !communityAllowedWeekdays.has(date.getDay()))
                                }
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                          <p className="mt-2 text-[11px] text-muted-foreground">
                            A neighbor delivers within ~6 days after your selected date, depending on driver availability.
                          </p>
                        </div>

                        {pricingEstimate?.available && (
                          <div className="rounded-md bg-muted/50 p-3 text-[11px] space-y-1">
                            <div className="flex justify-between"><span className="text-muted-foreground">Estimated distance</span><span>{pricingEstimate.estimatedMiles} mi</span></div>
                            <div className="flex justify-between"><span className="text-muted-foreground">Seller stops</span><span>{pricingEstimate.estimatedStops}</span></div>
                            <div className="flex justify-between font-semibold"><span>Delivery fee</span><span>{pricingEstimate.founderFreeDelivery ? "Free (founding perk)" : `$${pricingEstimate.deliveryFee?.toFixed(2)}`}</span></div>
                          </div>
                        )}

                        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                          <Truck className="h-3.5 w-3.5" />
                          <span>A community driver picks up from all sellers and delivers to you</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {deliveryMode === "per_seller" && fulfillmentGroups.length === 0 ? (
                    <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                      No producer fulfillment options are available for this cart yet.
                    </div>
                  ) : deliveryMode === "per_seller" ? (
                    fulfillmentGroups.map((group) => {
                      const selection = sellerSelections[group.groupId];
                      const selectedOption = group.availableOptions.find(
                        (option) => option.id === selection?.fulfillmentProfileId
                      );
                      const dateAllowed =
                        selection?.scheduledDate && selectedOption
                          ? isDateAllowed(selection.scheduledDate, selectedOption.dayLabel, selectedOption.specificDate)
                          : true;
                      const visibleOptions = group.availableOptions.filter((option) => {
                        if (pickupKind === "seller") return option.fulfillmentMethod === "seller_pickup";
                        if (pickupKind === "hub") return option.fulfillmentMethod === "drop_zone_pickup";
                        return true;
                      });

                      return (
                        <div key={group.groupId} className="rounded-xl border p-4 space-y-4">
                          <div>
                            <h3 className="font-semibold">{group.sellerName}</h3>
                            <p className="text-sm text-muted-foreground">
                              {group.items.map((item) => `${item.name} × ${item.qty}`).join(", ")}
                            </p>
                          </div>

                          <div className="space-y-3">
                            <p className="text-sm font-medium">Choose pickup option</p>

                            {visibleOptions.length === 0 && (
                              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                                {group.sellerName} doesn't offer {pickupKind === "seller" ? "seller pickup" : "community hub pickup"} yet. Pick another option above or remove this seller's items.
                              </div>
                            )}

                            {visibleOptions.map((option) => (
                              <label
                                key={option.id}
                                className="flex cursor-pointer items-start gap-3 rounded-lg border p-3"
                              >
                                <input
                                  type="radio"
                                  name={`group-${group.groupId}-fulfillment`}
                                  checked={selection?.fulfillmentProfileId === option.id}
                                  onChange={() =>
                                    setSellerSelections((current) => ({
                                      ...current,
                                      [group.groupId]: {
                                        fulfillmentProfileId: option.id,
                                        scheduledDate: current[group.groupId]?.scheduledDate || "",
                                        timeWindowLabel:
                                          (option.timeSlots?.length ?? 0) === 0 ? "Any time" : "",
                                      },
                                    }))
                                  }
                                  className="mt-1"
                                />

                                <div className="min-w-0">
                                  <div className="font-medium">
                                    {option.name || option.buyerLabel}
                                  </div>
                                  <div className="text-sm text-muted-foreground">
                                    {option.buyerLabel}
                                    {option.dayLabel ? ` · ${option.dayLabel}` : ""}
                                  </div>

                                  {option.dropZone && (
                                    <div className="text-sm text-muted-foreground">
                                      {option.dropZone.name} — {option.dropZone.city}, {option.dropZone.state}
                                    </div>
                                  )}

                                  {option.description && (
                                    <div className="text-sm text-muted-foreground">{option.description}</div>
                                  )}
                                </div>
                              </label>
                            ))}
                          </div>

                          <div className="grid gap-4 md:grid-cols-2">
                            <div className="space-y-2">
                              <label className="text-sm font-medium">{selectedOption?.fulfillmentMethod === "local_delivery" ? "Delivery date" : "Pickup date"}</label>
                              {(() => {
                                const allowedWeekdays = getAllowedWeekdaySet(selectedOption);
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                const groupItemIds = new Set(group.items.map((gi) => gi.itemId));
                                let leadTimeDays = 0;
                                for (const ci of items) {
                                  if (!groupItemIds.has(ci.item.id)) continue;
                                  if ((ci.item as any).isMadeToOrder === 1) {
                                    leadTimeDays = Math.max(
                                      leadTimeDays,
                                      Number((ci.item as any).leadTimeDays) || 0
                                    );
                                  }
                                }
                                const earliestDate = new Date(today);
                                if (leadTimeDays > 0) {
                                  earliestDate.setDate(earliestDate.getDate() + leadTimeDays);
                                }
                                const selectedDate = selection?.scheduledDate
                                  ? new Date(`${selection.scheduledDate}T12:00:00`)
                                  : undefined;
                                return (
                                  <>
                                  {leadTimeDays > 0 && (
                                    <p className="text-xs text-muted-foreground">
                                      Made to order — ready in about {leadTimeDays} day
                                      {leadTimeDays === 1 ? "" : "s"}. Earliest pickup{" "}
                                      <span className="font-medium">{format(earliestDate, "MMM d")}</span>.
                                    </p>
                                  )}
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <button
                                        type="button"
                                        className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-left text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                        disabled={!selectedOption}
                                      >
                                        <span className={selectedDate ? "" : "text-muted-foreground"}>
                                          {selectedDate ? format(selectedDate, "MM/dd/yyyy") : "Pick a date"}
                                        </span>
                                        <Calendar className="h-4 w-4 text-muted-foreground" />
                                      </button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                                      <CalendarPicker
                                        mode="single"
                                        selected={selectedDate}
                                        defaultMonth={
                                          selectedDate ||
                                          (selectedOption?.specificDate
                                            ? new Date(`${selectedOption.specificDate}T12:00:00`)
                                            : earliestDate)
                                        }
                                        onSelect={(date) => {
                                          if (!date) return;
                                          setSellerSelections((current) => {
                                            const profileId =
                                              current[group.groupId]?.fulfillmentProfileId ||
                                              group.availableOptions[0]?.id ||
                                              "";
                                            const opt = group.availableOptions.find(
                                              (o) => o.id === profileId
                                            );
                                            const hasNoSlots = (opt?.timeSlots?.length ?? 0) === 0;
                                            return {
                                              ...current,
                                              [group.groupId]: {
                                                fulfillmentProfileId: profileId,
                                                scheduledDate: format(date, "yyyy-MM-dd"),
                                                timeWindowLabel: hasNoSlots ? "Any time" : "",
                                              },
                                            };
                                          });
                                        }}
                                        disabled={(date) => {
                                          if (date < earliestDate) return true;
                                          // One-time event: that single date only.
                                          if (selectedOption?.specificDate) {
                                            return format(date, "yyyy-MM-dd") !== selectedOption.specificDate;
                                          }
                                          if (allowedWeekdays && !allowedWeekdays.has(date.getDay())) return true;
                                          return false;
                                        }}
                                        initialFocus
                                      />
                                    </PopoverContent>
                                  </Popover>
                                  </>
                                );
                              })()}
                              {selectedOption?.specificDate ? (
                                <p className="text-xs text-muted-foreground">
                                  One-time pickup on{" "}
                                  <span className="font-medium">
                                    {format(
                                      new Date(`${selectedOption.specificDate}T12:00:00`),
                                      "EEEE, MMMM d, yyyy",
                                    )}
                                  </span>{" "}
                                  only.
                                </p>
                              ) : (
                                selectedOption?.dayLabel && selectedOption.dayLabel !== "Any day" && (
                                  <p className="text-xs text-muted-foreground">
                                    Available on{" "}
                                    <span className="font-medium">{selectedOption.dayLabel}</span>.
                                  </p>
                                )
                              )}
                              {selection?.scheduledDate && selectedOption && !dateAllowed && (
                                <p className="text-sm text-destructive">
                                  This option is only available on{" "}
                                  <span className="font-semibold">{selectedOption.dayLabel}</span>.
                                </p>
                              )}
                            </div>

                            <div className="space-y-2">
                              <label className="text-sm font-medium">Time window</label>
                              {(() => {
                                const availableTimeWindows = getAvailableTimeWindowOptions(
                                  selectedOption,
                                  selection?.scheduledDate || ""
                                );
                                const hasSpecificWindows = (selectedOption?.timeSlots || []).length > 0;

                                if (!hasSpecificWindows) {
                                  return (
                                    <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
                                      Any time
                                    </div>
                                  );
                                }

                                return (
                                  <select
                                    value={selection?.timeWindowLabel || ""}
                                    onChange={(e) =>
                                      setSellerSelections((current) => ({
                                        ...current,
                                        [group.groupId]: {
                                          fulfillmentProfileId:
                                            current[group.groupId]?.fulfillmentProfileId ||
                                            group.availableOptions[0]?.id ||
                                            "",
                                          scheduledDate: current[group.groupId]?.scheduledDate || "",
                                          timeWindowLabel: e.target.value,
                                        },
                                      }))
                                    }
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                    disabled={!selection?.scheduledDate || availableTimeWindows.length === 0}
                                  >
                                    <option value="">
                                      {!selection?.scheduledDate
                                        ? "Choose a date first"
                                        : availableTimeWindows.length === 0
                                          ? "No time windows for that day"
                                          : "Choose a time window"}
                                    </option>
                                    {availableTimeWindows.map((window) => (
                                      <option key={window.id} value={window.label}>
                                        {window.label}
                                      </option>
                                    ))}
                                  </select>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : null}

                  {deliveryMode === "per_seller" && !selectionState.allComplete && fulfillmentGroups.length > 0 && (
                    <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                      Choose a fulfillment method, date, and time window for each producer before paying.
                    </div>
                  )}
                </CardContent>
              </Card>

              {availableRewards.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Gift className="h-5 w-5" />
                      Apply a reward
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <p className="text-sm text-muted-foreground">
                      Pick one to apply. You can change it any time before paying. Only one
                      reward can be applied per order.
                    </p>
                    {availableRewards.map((reward) => {
                      const isApplied = reward.id === appliedRewardId;
                      return (
                        <div
                          key={reward.id}
                          className={`flex items-center justify-between gap-3 rounded-md border p-3 ${
                            isApplied ? "border-primary bg-primary/5" : ""
                          }`}
                        >
                          <div className="min-w-0">
                            <div className="font-medium">
                              {reward.discountPercent}% off
                            </div>
                            <div className="truncate text-xs text-muted-foreground">
                              {reward.label}
                            </div>
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            variant={isApplied ? "outline" : "default"}
                            onClick={() =>
                              setAppliedRewardId(isApplied ? null : reward.id)
                            }
                          >
                            {isApplied ? "Remove" : "Apply"}
                          </Button>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader>
                  <CardTitle>Payment Information</CardTitle>
                </CardHeader>
                <CardContent>
                  {(!selectionState.allComplete || !madeToOrderComplete) ? (
                    <div className="rounded-lg border border-dashed border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 space-y-1">
                      <p className="font-medium">A few details to finish before you pay:</p>
                      {!madeToOrderComplete && (
                        <p>Fill in the required customization for your made-to-order item above.</p>
                      )}
                      {!selectionState.allComplete &&
                        (missingCheckoutDetails.length > 0 ? (
                          <ul className="ml-4 list-disc space-y-0.5">
                            {missingCheckoutDetails.map((detail, idx) => (
                              <li key={idx}>{detail}</li>
                            ))}
                          </ul>
                        ) : (
                          <p>Complete the pickup or delivery section above to load payment options.</p>
                        ))}
                    </div>
                  ) : needsFoodRiskAck && !foodRiskAck ? (
                    <FoodRiskAck label={riskyLabel!} checked={foodRiskAck} onChange={setFoodRiskAck} />
                  ) : !clientSecret ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                  ) : (
                    <Elements stripe={stripePromise} options={{ clientSecret }}>
                      <CheckoutForm
                        onSuccess={handlePaymentSuccess}
                        total={total}
                        disabled={!selectionState.allComplete}
                        orderId={orderId}
                        clientSecret={clientSecret}
                      />
                    </Elements>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
